import { CalendarEventVisit, CrewMember, DayViewEvent, Timesheet, Visit, VisitWorkareaStatusRecord } from "../scheduleApi"
import { MarkerStatus } from "./ScheduleMapMarker"
import dayjs, { Dayjs } from "dayjs"
import { getVisitForEvent, isEventClockedIn, eventHasVisit, getDayDateRange } from "../scheduleModel"

export const getMarkerStatus = (event: DayViewEvent, visits: Visit[], calendarEventVisits: CalendarEventVisit[], timesheets: Timesheet[], crew: CrewMember[], date: Dayjs): MarkerStatus => {
  const dateRange = getDayDateRange(date)
  const hasVisit = eventHasVisit(event, calendarEventVisits, visits, dateRange)

  if (!hasVisit) {
    return 'planned'
  }

  if (isEventClockedIn(event, calendarEventVisits, visits, timesheets, crew, dateRange)) {
    return 'clocked-in'
  }

  return 'visited'
}

export type EventWithCoordinates = {
  event: DayViewEvent
  latitude: number
  longitude: number
}

export type CrewLeadEventGroup = {
  crewLeadEmail: string
  events: DayViewEvent[]
  color: string
}

export const groupEventsByCrewLead = (events: DayViewEvent[], crew?: CrewMember[]): CrewLeadEventGroup[] => {
  const eventsWithCrewLead = events.filter(event =>
    event.crewForemanEmail !== null &&
    event.latitude !== null &&
    event.longitude !== null
  )

  const grouped = eventsWithCrewLead.reduce((acc, event) => {
    const email = event.crewForemanEmail!.toLowerCase()
    if (!acc[email]) {
      const crewMember = crew?.find(c => c.userEmail?.toLowerCase() === email)
      acc[email] = {
        crewLeadEmail: email,
        events: [],
        color: crewMember?.color || '#000000'
      }
    }
    acc[email].events.push(event)
    return acc
  }, {} as Record<string, CrewLeadEventGroup>)

  return Object.values(grouped).map(group => ({
    ...group,
    events: group.events.sort((a, b) => dayjs(a.start).valueOf() - dayjs(b.start).valueOf())
  }))
}

export const parseMapPosition = (mapPosition: string | null): { latitude: number, longitude: number } | null => {
  if (!mapPosition) return null

  const parts = mapPosition.split(',')
  if (parts.length !== 2) return null

  const latitude = parseFloat(parts[0].trim())
  const longitude = parseFloat(parts[1].trim())

  if (isNaN(latitude) || isNaN(longitude)) return null

  return { latitude, longitude }
}

export type EventMarker = {
  event: DayViewEvent
  latitude: number
  longitude: number
  status: MarkerStatus
  crewLeadAvatar?: string | null
  crewLeadName?: string | null
  crewLeadColor?: string | null
  eventName: string
}

export type ClusterWorkArea = {
  id: number
  name: string
  status: number | null
}

export type MarkerCluster = {
  latitude: number
  longitude: number
  address: string
  events: Array<{
    id: number
    status: MarkerStatus
    crewLeadAvatar?: string | null
    crewLeadName?: string | null
    crewLeadColor?: string | null
    eventName: string
    customerDisplayName?: string | null
    start: string
    end: string
    workAreas: ClusterWorkArea[]
  }>
}

const POSITION_TOLERANCE = 0.00001

const arePositionsEqual = (lat1: number, lng1: number, lat2: number, lng2: number): boolean => {
  return Math.abs(lat1 - lat2) < POSITION_TOLERANCE && Math.abs(lng1 - lng2) < POSITION_TOLERANCE
}

const getAddressFromEvent = (event: DayViewEvent): string => {
  return [
    event.customerAddress1,
    event.customerCity,
    event.customerState,
    event.customerZip
  ].filter(Boolean).join(', ')
}

type ClusterParams = {
  markers: EventMarker[]
  visits: Visit[]
  calendarEventVisits: CalendarEventVisit[]
  workareaStatuses: VisitWorkareaStatusRecord[]
  date: Dayjs
}

const getWorkAreasForEvent = (
  event: DayViewEvent,
  visits: Visit[],
  calendarEventVisits: CalendarEventVisit[],
  workareaStatuses: VisitWorkareaStatusRecord[],
  date: Dayjs
): ClusterWorkArea[] => {
  const visit = getVisitForEvent(event, calendarEventVisits, visits, getDayDateRange(date))

  const visitWorkareaStatuses = visit
    ? workareaStatuses.filter(ws => ws.visitId === visit.id)
    : []

  const eventWorkareaIds = (event.workAreaIds && event.workAreaIds !== '')
    ? event.workAreaIds.split(',').map(id => parseInt(id.trim()))
    : []

  const workareaNames = (event.workAreaNames && event.workAreaNames !== '')
    ? event.workAreaNames.split(',')
    : []

  if (visit && visitWorkareaStatuses.length > 0) {
    return visitWorkareaStatuses.map(ws => ({
      id: ws.workareaId,
      name: ws.workareaName ?? `Work Area ${ws.workareaId}`,
      status: ws.status
    }))
  }

  return eventWorkareaIds.map((id, index) => {
    const nameFromList = workareaNames[index]?.trim()
    const name = (nameFromList && nameFromList !== '')
      ? nameFromList
      : `Work Area ${id}`

    return {
      id,
      name,
      status: null
    }
  })
}

const buildClusterEvent = (
  marker: EventMarker,
  visits: Visit[],
  calendarEventVisits: CalendarEventVisit[],
  workareaStatuses: VisitWorkareaStatusRecord[],
  date: Dayjs
) => ({
  id: marker.event.id,
  status: marker.status,
  crewLeadAvatar: marker.crewLeadAvatar,
  crewLeadName: marker.crewLeadName,
  crewLeadColor: marker.crewLeadColor,
  eventName: marker.eventName,
  customerDisplayName: marker.event.customerDisplayName,
  start: marker.event.start,
  end: marker.event.end,
  workAreas: getWorkAreasForEvent(marker.event, visits, calendarEventVisits, workareaStatuses, date)
})

export const clusterMarkersByPosition = ({ markers, visits, calendarEventVisits, workareaStatuses, date }: ClusterParams): { clusters: MarkerCluster[], singles: EventMarker[] } => {
  const clusters: MarkerCluster[] = []
  const singles: EventMarker[] = []
  const processed = new Set<number>()

  markers.forEach((marker, index) => {
    if (processed.has(index)) return

    const matchingMarkers = markers.filter((m, i) =>
      i !== index &&
      !processed.has(i) &&
      arePositionsEqual(marker.latitude, marker.longitude, m.latitude, m.longitude)
    )

    if (matchingMarkers.length > 0) {
      processed.add(index)
      matchingMarkers.forEach(() => {
        const originalIndex = markers.findIndex((m, idx) =>
          idx > index &&
          !processed.has(idx) &&
          arePositionsEqual(marker.latitude, marker.longitude, m.latitude, m.longitude)
        )
        if (originalIndex !== -1) {
          processed.add(originalIndex)
        }
      })

      clusters.push({
        latitude: marker.latitude,
        longitude: marker.longitude,
        address: getAddressFromEvent(marker.event),
        events: [
          buildClusterEvent(marker, visits, calendarEventVisits, workareaStatuses, date),
          ...matchingMarkers.map(m => buildClusterEvent(m, visits, calendarEventVisits, workareaStatuses, date))
        ]
      })
    } else {
      singles.push(marker)
    }
  })

  return { clusters, singles }
}
